export const UI_PREVIEW_ENGLISH = {
  "settings.uiPreview.workspace.git": "Git project",
  "settings.uiPreview.workspace.note":
    "Preview the Workspace control and available pages. No file or terminal request is sent.",
  "settings.uiPreview.title": "UI reference",
  "settings.uiPreview.description": "Explore the shared styles and controls in every theme.",
  "settings.uiPreview.open": "Open UI reference",
  "settings.uiPreview.openFailed": "The UI reference could not be opened. Try again.",
  "settings.uiPreview.scope": "Preview changes stay here. Your saved appearance stays the same.",
  "settings.uiPreview.instructions": "Hover, press, or use Tab to explore the live controls.",
  "settings.uiPreview.output.title": "Long output",
  "settings.uiPreview.output.description": "Try the same reading controls used for long thoughts and tool results.",
  "settings.uiPreview.output.line": "Line {{line}}: Inspect the complete result inside this reading area.",
  "settings.uiPreview.output.label": "Scrollable example output",
  "settings.uiPreview.output.thought.title": "Thought",
  "settings.uiPreview.output.thought.label": "Scrollable example thought",
  "settings.uiPreview.output.response.title": "Response",
  "settings.uiPreview.output.prose.sample":
    "## Reading the result\n\nCompare the evidence before drawing a conclusion. Longer paragraphs keep a comfortable reading width while equations, tables, and code can use the available space.\n\n**Key observation.** Emphasis keeps the same paragraph spacing.\n\n- Check the assumptions.\n- Verify the result with a concrete example.\n\n$$f(x) = x^2 + 2x + 1$$\n\n```ts\nconst result = values.map((value) => value * value)\nconsole.log(result)\n```",
  "settings.uiPreview.output.file.title": "File content",
  "settings.uiPreview.output.file.label": "Scrollable file content",
  "settings.uiPreview.output.file.sample":
    'Read file src/notes.txt, lines 1-3\n1: # Notes\n2:     Keep the indentation exactly as written.\n3: export const detailedExample = { source: "src/components/workspace/file-browser/document-preview/example-very-long-name.tsx", visible: true }',
  "settings.uiPreview.output.results.title": "Search results",
  "settings.uiPreview.output.results.label": "Scrollable search results",
  "settings.uiPreview.output.results.sample":
    "Found 2 matches\nsrc/components/workspace/file-browser/document-preview/example-very-long-name.tsx:\n  Line 42: A matching line in a long path.\n\nsrc/components/workspace/file-browser/search-results.tsx:\n  Line 18: Another matching line.",
  "settings.uiPreview.theme": "Preview theme",
  "settings.uiPreview.scheme": "Preview color scheme",
  "settings.uiPreview.components": "Controls",
  "settings.uiPreview.icons": "Icons",
  "settings.uiPreview.tokens": "Colors and type",
  "settings.uiPreview.buttons": "Buttons and actions",
  "settings.uiPreview.primary": "Primary",
  "settings.uiPreview.secondary": "Secondary",
  "settings.uiPreview.quiet": "Quiet",
  "settings.uiPreview.danger": "Destructive",
  "settings.uiPreview.warning": "Warning",
  "settings.uiPreview.disabled": "Disabled",
  "settings.uiPreview.loading": "Loading",
  "settings.uiPreview.rest": "Rest",
  "settings.uiPreview.hover": "Hover",
  "settings.uiPreview.pressed": "Pressed",
  "settings.uiPreview.focus": "Keyboard focus",
  "settings.uiPreview.buttonState": "{{variant}}: {{state}}",
  "settings.uiPreview.composer.title": "Composer action",
  "settings.uiPreview.composer.description":
    "The same circular action sends, stops, or resumes a response. Drafts are queued while the response runs.",
  "settings.uiPreview.composer.geometry": "32 px circle · 20 px icon · 36 px click target · 44 px touch target",
  "settings.uiPreview.composer.motion.title": "Execution feedback",
  "settings.uiPreview.composer.motion.system": "Standard motion",
  "settings.uiPreview.composer.motion.system.description":
    "A brief cue on entering the running state. Respects your system's motion preference.",
  "settings.uiPreview.composer.motion.system.start": "Start motion preview",
  "settings.uiPreview.composer.motion.system.stop": "Stop motion preview",
  "settings.uiPreview.composer.motion.reduce": "Reduced motion",
  "settings.uiPreview.composer.motion.reduce.description": "The same action and running state, without animation.",
  "settings.uiPreview.composer.motion.reduce.start": "Start reduced-motion preview",
  "settings.uiPreview.composer.motion.reduce.stop": "Stop reduced-motion preview",
  "settings.uiPreview.composer.motion.note":
    "The 1,200 ms cue plays once. Stop stays still and clickable; the cue does not show a progress percentage.",
  "settings.uiPreview.composer.motion.idle": "Start either local preview to compare both. No request is sent.",
  "settings.uiPreview.composer.motion.running": "Local preview running. Stop either control to reset both.",
  "settings.uiPreview.composer.states": "Send, queue, stop, and resume interaction states",
  "settings.uiPreview.composer.action": "Action",
  "settings.uiPreview.composer.send": "Send",
  "settings.uiPreview.composer.queue": "Queue message",
  "settings.uiPreview.composer.run": "Run command",
  "settings.uiPreview.composer.stop": "Stop",
  "settings.uiPreview.composer.resume": "Resume",
  "settings.uiPreview.composer.empty": "Empty input",
  "settings.uiPreview.composer.empty.description": "Disabled until there is content to send.",
  "settings.uiPreview.composer.unavailable": "Unavailable",
  "settings.uiPreview.composer.unavailable.description": "Disabled when sending is unavailable.",
  "settings.uiPreview.composer.shellReady": "Shell ready",
  "settings.uiPreview.composer.shellReady.description": "Run a shell command.",
  "settings.uiPreview.composer.shellEmpty": "Shell empty",
  "settings.uiPreview.composer.shellEmpty.description": "Disabled until a command is entered.",
  "settings.uiPreview.composer.try": "Try the interaction",
  "settings.uiPreview.composer.local": "Local preview only. No message or command is sent.",
  "settings.uiPreview.composer.input": "Preview message",
  "settings.uiPreview.composer.placeholder": "Type a message to enable Send…",
  "settings.uiPreview.composer.idle": "Empty input keeps Send disabled.",
  "settings.uiPreview.composer.ready": "Send clears this preview input and shows Stop.",
  "settings.uiPreview.composer.running": "Response preview active. Type to queue a message, or Stop to pause.",
  "settings.uiPreview.composer.stopped": "Response stopped. Resume continues it; typing switches the button to Send.",
  "settings.uiPreview.composer.runningWithDraft":
    "Running with a draft. Queue it for later, then use Send now on a queued message to supplement this response.",
  "settings.uiPreview.composer.editing": "This message is back in the input. Send it again when ready.",
  "settings.uiPreview.composer.added": "Added to the local response preview: {{message}}",
  "settings.uiPreview.composer.previewSend": "Send preview message",
  "settings.uiPreview.composer.previewQueue": "Queue preview message",
  "settings.uiPreview.composer.previewSave": "Save queued preview message",
  "settings.uiPreview.composer.cancelEdit": "Cancel edit",
  "settings.uiPreview.composer.previewStop": "Stop preview response",
  "settings.uiPreview.composer.previewResume": "Resume preview response",
  "settings.uiPreview.fields": "Fields and validation",
  "settings.uiPreview.name": "Project name",
  "settings.uiPreview.name.placeholder": "Enter a project name",
  "settings.uiPreview.name.value": "Algebra study",
  "settings.uiPreview.invalid": "Invalid field",
  "settings.uiPreview.invalid.message": "Enter a project name to continue.",
  "settings.uiPreview.notes": "Notes",
  "settings.uiPreview.notes.placeholder": "Try a longer note or compose text in your language.",
  "settings.uiPreview.options": "Choices and preferences",
  "settings.uiPreview.checkbox": "Include supporting notes",
  "settings.uiPreview.switch": "Show details",
  "settings.uiPreview.radio": "Example selection",
  "settings.uiPreview.option.first": "First option",
  "settings.uiPreview.option.second": "Second option",
  "settings.uiPreview.rows": "Selectable rows",
  "settings.uiPreview.project.local": "Local project",
  "settings.uiPreview.project.remote": "Remote project",
  "settings.uiPreview.project.selected": "Selected project",
  "settings.uiPreview.overlays": "Menus and dialogs",
  "settings.uiPreview.menu": "Open example menu",
  "settings.uiPreview.menu.option": "Show optional details",
  "settings.uiPreview.dialog": "Open example dialog",
  "settings.uiPreview.dialog.title": "Example dialog",
  "settings.uiPreview.dialog.description": "This uses the same dialog and focus behavior as the rest of the app.",
  "settings.uiPreview.dialog.body": "Try Tab and Escape. Closing returns you to the control that opened this dialog.",
  "settings.uiPreview.dialog.confirm": "Try primary action",
  "settings.uiPreview.dialog.done": "Example dialog action completed.",
  "settings.uiPreview.action": "Preview action: {{action}}",
  "settings.uiPreview.icons.search": "Search icon names",
  "settings.uiPreview.icons.size": "Icon size",
  "settings.uiPreview.icons.size.value": "{{size}} px",
  "settings.uiPreview.icons.folders": "Project folders",
  "settings.uiPreview.icons.local.closed": "Local, closed",
  "settings.uiPreview.icons.local.open": "Local, open",
  "settings.uiPreview.icons.remote.closed": "Remote, closed",
  "settings.uiPreview.icons.remote.open": "Remote, open",
  "settings.uiPreview.icons.empty": "No icons match your search.",
  "settings.uiPreview.icons.count.one": "{{count}} icon",
  "settings.uiPreview.icons.count.other": "{{count}} icons",
  "settings.uiPreview.colors": "Semantic colors",
  "settings.uiPreview.color.canvas": "Canvas",
  "settings.uiPreview.color.surface": "Secondary surface",
  "settings.uiPreview.color.text": "Primary text",
  "settings.uiPreview.color.muted": "Muted text",
  "settings.uiPreview.color.border": "Border",
  "settings.uiPreview.color.action": "Primary action",
  "settings.uiPreview.color.hover": "Hover surface",
  "settings.uiPreview.color.selected": "Selected surface",
  "settings.uiPreview.color.success": "Success",
  "settings.uiPreview.color.warning": "Warning",
  "settings.uiPreview.color.danger": "Danger",
  "settings.uiPreview.typography": "Typography",
  "settings.uiPreview.type.title": "A clear place to explore",
  "settings.uiPreview.type.heading": "A consistent visual language",
  "settings.uiPreview.type.body": "Readable text, quiet surfaces, and clear actions help you focus on your work.",
  "settings.uiPreview.type.caption": "Compact text keeps descenders visible: g j p q y.",
  "settings.uiPreview.type.code": "Code and mathematical content keep their own typography.",
  "settings.uiPreview.rules": "Layout and interaction rules",
  "settings.uiPreview.rule.spacing": "Use a 4 px spacing scale. Leave 16 px between fields and 24 px between sections.",
  "settings.uiPreview.rule.controls":
    "Standard button: 36 px high / 8 px radius. Compact button: 28 px. Field: 38 px. List row: 34 px.",
  "settings.uiPreview.rule.surfaces":
    "Menu radius: 10 px. Dialog radius: 16 px, with 24 px body padding. Keep the primary action at the right.",
  "settings.uiPreview.rule.focus":
    "Show a 2 px keyboard focus ring. Hover and pressed states never change size. Touch targets are at least 44 px.",
  "settings.uiPreview.rule.motion":
    "Use brief feedback without layout shifts. Respect reduced motion. Loading keeps its label; errors keep the input.",
  "settings.uiPreview.rule.content":
    "Use shared outline icons and semantic colors. Preserve brand marks, project images, code, math, and native window controls.",
  "settings.uiPreview.type.title.metric": "Large heading · text-20-medium",
  "settings.uiPreview.type.heading.metric": "Heading · text-16-medium",
  "settings.uiPreview.type.body.metric": "Body · text-14-regular",
  "settings.uiPreview.type.caption.metric": "Compact text · text-12-regular",
  "settings.uiPreview.type.code.metric": "Code · text-12-mono",
  "settings.uiPreview.themes.error": "Some themes could not be loaded.",
} as const
